import { API_URLS } from '../urls';

import { ChatModChannel      ,
         ChatModMessages     ,
         ChatModBan          ,
         ChatDecision        ,
         ChatSanction        ,
         ChatModerationLevel } from '../../dataTypes/moderationManagment/chat_types';

/**
 * Écran Modération, onglet Chat en direct (admin-api, ChatModerationController).
 *
 * Relevé périodique plutôt que WebSocket, comme les messages de la communauté :
 * une file de modération se relit toutes les 3 s sans dommage, et l'API du
 * chat n'a pas de canal temps réel. `no-store` : sans lui, le navigateur sert
 * sa copie et la file ne bouge plus alors que le relevé tourne.
 *
 * Les erreurs remontent en exception avec le code HTTP dans le message : un
 * 404 veut dire « pas encore déployé », et l'écran le dit tel quel.
 */

export class ChatModerationError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

const getJson = async <T>(url: string): Promise<T> => {
  const res  = await fetch(url, { cache: 'no-store' });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new ChatModerationError(data.error ?? `HTTP ${res.status}`, res.status);

  return data as T;
};

const postJson = async <T>(url: string, body: object): Promise<T> => {
  const res  = await fetch(url, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new ChatModerationError(data.error ?? `HTTP ${res.status}`, res.status);

  return data as T;
};

export const fetchChatModChannels = async (adminId: number): Promise<ChatModChannel[]> => {
  const data = await getJson<{ channels: ChatModChannel[] }>(`${API_URLS.getChatModerationChannels}?adminId=${adminId}`);

  return Array.isArray(data.channels) ? data.channels : [];
};

export const fetchChatModMessages = async (adminId: number, channelIds: number[]): Promise<ChatModMessages> => {
  const data = await getJson<ChatModMessages>(
    `${API_URLS.getChatModerationMessages}?adminId=${adminId}&channelIds=${channelIds.join(',')}`);

  return {
    pending    : Array.isArray(data.pending) ? data.pending : [],
    recent     : Array.isArray(data.recent)  ? data.recent  : [],
    serverTime : data.serverTime,
  };
};

/** Rend les ids réellement traités (ceux des chaînes du créateur). */
export const decideChatMessages = async (adminId: number, messageIds: number[], action: ChatDecision): Promise<number[]> => {
  const data = await postJson<{ done: number[] }>(API_URLS.decideChatMessages, { adminId, messageIds, action });

  return data.done ?? [];
};

export interface ChatSanctionResult {
  pseudo            : string;
  closedConnections : number;
  rejectedPending   : number;
  banned            : boolean;
}

export const sanctionChatAuthor = (adminId: number, messageId: number, action: ChatSanction) =>
  postJson<ChatSanctionResult>(API_URLS.sanctionChatAuthor, { adminId, messageId, action });

export const updateChatModChannel = (adminId: number, channelId: number,
                                     change: { chatOpen?: boolean; level?: ChatModerationLevel }) =>
  postJson<{ ok: boolean }>(API_URLS.updateChatModerationChannel, { adminId, channelId, ...change });

export const fetchChatBans = async (adminId: number): Promise<ChatModBan[]> => {
  const data = await getJson<{ bans: ChatModBan[] }>(`${API_URLS.getChatModerationBans}?adminId=${adminId}`);

  return Array.isArray(data.bans) ? data.bans : [];
};

export const liftChatBan = (adminId: number, banId: number) =>
  postJson<{ ok: boolean }>(API_URLS.liftChatBan, { adminId, banId });
